import React, { useContext, useState } from 'react';
import FormControl from '@material-ui/core/FormControl';
import FormLabel from '@material-ui/core/FormLabel';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import RadioGroup from '@material-ui/core/RadioGroup';
import Radio from '@material-ui/core/Radio';
import Button from '@material-ui/core/Button';
import { saveAs } from 'file-saver';
import parser from 'content-disposition';
import { get } from 'lodash';

import { MetadataApiRoute, ReportTemplateType } from 'enums';
import { DomainObject } from 'types';
import fetch from 'utils/fetch';
import { NotificationCtx } from 'components/notification/notification-hook';
import { NotificationVariant } from 'components/notification/notification-types';

import { DialogContentProps } from '../types';
import { DefaultDialog } from './aisuk-default-dialog';
import { Loader } from 'components/loader/loader';
import { Typography } from '@material-ui/core';

type DownloadFormat = 'XML' | 'PDF';

type MetadataDialogContentType<T extends DomainObject> = {
  fileName: string;
  templateType: ReportTemplateType;
  route: MetadataApiRoute;
  data: T;
};

export const DownloadMetadaDialog = <T extends DomainObject>({
  initialValues,
  onClose,
}: DialogContentProps<MetadataDialogContentType<T>>) => {
  const [format, setFormat] = useState<DownloadFormat>('XML');
  const { showNotification } = useContext(NotificationCtx);
  const [loading, setLoading] = useState(false);

  const { route, data, templateType, fileName } = initialValues ?? {};

  const params = {
    entityId: data!.id,
    fileName,
  };

  const handleChangeFormat = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormat(e.target.value as DownloadFormat);
  };

  const handleDownload = async () => {
    let downloadLink = '';

    if (format === 'XML') {
      downloadLink = `${route}/${data!.id}/xml`;
      window.open(process.env.PUBLIC_URL + downloadLink, '_blank');
    }

    if (format === 'PDF') {
      downloadLink = `/api/report/generate?templateType=${templateType}&type=PDF`;
      setLoading(true);
      try {
        const response = await fetch(downloadLink, {
          method: 'POST',
          headers: new Headers({ 'Content-Type': 'application/json' }),
          body: JSON.stringify(params),
        });

        const file = await response.blob();
        const fileNameFromResponse = extractFileName(response);
        setLoading(false);
        saveAs(file, fileNameFromResponse ?? 'output');
      } catch (e) {
        showNotification(
          'Při pokusu o stáhnutí nastala chyba.',
          NotificationVariant.ERROR
        );
        setLoading(false);
      }
    }

    onClose();
  };

  return (
    <DefaultDialog
      title="Stažení metadat/popisu"
      actions={
        <Button color="primary" onClick={handleDownload} disabled={loading}>
          Stáhnout
        </Button>
      }
    >
      {loading ? (
        <div
          style={{
            minHeight: 150,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <div>
            <Typography style={{ marginBottom: 8 }}>
              Probíhá generovaní PDF...
            </Typography>
            <Loader />
          </div>
        </div>
      ) : (
        <FormControl component="fieldset">
          <FormLabel component="legend">Formát</FormLabel>
          <RadioGroup
            aria-label="format"
            value={format}
            onChange={handleChangeFormat}
          >
            <FormControlLabel
              label="XML"
              value="XML"
              control={<Radio color="primary" />}
            />
            <FormControlLabel
              label="PDF"
              value="PDF"
              control={<Radio color="primary" />}
            />
          </RadioGroup>
        </FormControl>
      )}
    </DefaultDialog>
  );
};

/**
 *
 * @param response
 * @returns
 */
function extractFileName(response: Response) {
  const headers = response.headers;
  const contentDisposition = headers.get('content-disposition');

  if (contentDisposition) {
    const disposition = parser.parse(contentDisposition);
    return get(disposition, 'parameters.filename') as string;
  } else {
    return undefined;
  }
}

/**
 *
 * @param name
 * @returns
 */
export function processRequestFileName(name: string | number) {
  let str = '0000000' + name.toString();

  return str.slice(-8);
}
